import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ColorSwatchPickerComponent, ColorSelectedEvent } from '../../_shared/ui/color-swatch-picker/color-swatch-picker.component';
import { imageBorderCss, normalizeImageBorderColor } from './extensions/phase1-image';

export interface ImageEditAttrs {
  src?: string | null;
  width?: string | number | null;
  height?: string | number | null;
  keepAspectRatio?: boolean;
  imageSourceType?: 'linked' | 'attachment' | null;
  attachmentId?: string | null;
  originalUrl?: string | null;
  /** 2px border around the image in this color; null = no border. */
  borderColor?: string | null;
  // NOTE: fileSizeBytes, naturalWidth, naturalHeight are attachment metadata.
  // They are passed as separate @Input() properties, not stored in the Tiptap node.
}

@Component({
  selector: 'app-image-edit-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule, ButtonModule, CheckboxModule, ColorSwatchPickerComponent],
  template: `
    <p-dialog
      header="Edit Image"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      (onHide)="onDialogHide()"
      [style]="{ width: '440px', maxWidth: '92vw' }"
      appendTo="body">

      <!-- Section A: Display size -->
      <div class="flex flex-col gap-3 mb-4">
        <div class="text-xs font-semibold uppercase tracking-wide text-surface-500 mb-1">Display Size</div>

        <div class="flex gap-3 items-end">
          <!-- Width -->
          <div class="flex flex-col gap-1 flex-1">
            <label for="img-width" class="text-sm font-medium">Width (px)</label>
            <input
              id="img-width"
              pInputText
              type="number"
              min="1"
              [(ngModel)]="displayWidth"
              (ngModelChange)="onWidthChange($event)"
              placeholder="auto"
              class="w-full" />
          </div>

          <!-- Height -->
          <div class="flex flex-col gap-1 flex-1">
            <label for="img-height" class="text-sm font-medium">Height (px)</label>
            <input
              id="img-height"
              pInputText
              type="number"
              min="1"
              [(ngModel)]="displayHeight"
              (ngModelChange)="onHeightChange($event)"
              placeholder="auto"
              class="w-full" />
          </div>
        </div>

        <!-- Keep aspect ratio -->
        <div class="flex items-center gap-2">
          <p-checkbox
            [(ngModel)]="keepAspectRatio"
            [binary]="true"
            inputId="keep-aspect"
            (ngModelChange)="onKeepAspectRatioChange()">
          </p-checkbox>
          <label for="keep-aspect" class="text-sm select-none cursor-pointer">
            Keep aspect ratio
          </label>
        </div>

        <!-- Reset to natural size -->
        <div>
          <p-button
            label="Reset to natural size"
            severity="secondary"
            size="small"
            [disabled]="!hasNaturalSize"
            (onClick)="resetToNaturalSize()">
          </p-button>
          <span *ngIf="!hasNaturalSize" class="text-xs text-surface-400 ml-2">
            Natural size unavailable
          </span>
        </div>
      </div>

      <!-- Divider -->
      <div class="border-t border-surface-200 my-3"></div>

      <!-- Section B: Border -->
      <div class="flex flex-col gap-2 mb-4">
        <div class="text-xs font-semibold uppercase tracking-wide text-surface-500 mb-1">Border</div>
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-sm font-medium w-24 shrink-0">Color</span>
          <app-color-swatch-picker
            class="image-border-picker"
            [value]="borderColor"
            [active]="!!borderColor"
            mode="generic"
            [allowClear]="true"
            [showRecent]="true"
            [showAdvanced]="true"
            (colorSelected)="onBorderColorSelected($event)">
          </app-color-swatch-picker>
          <span class="text-sm text-surface-700" data-testid="image-border-label">{{ borderColor || 'None' }}</span>
          <p-button
            label="No border"
            severity="secondary"
            size="small"
            [text]="true"
            [disabled]="!borderColor"
            (onClick)="borderColor = null">
          </p-button>
        </div>
        <div class="image-border-preview" aria-hidden="true">
          <span [style.border]="borderColor ? borderCss(borderColor) : '1px dashed var(--p-surface-300, #d1d5db)'"></span>
          <span class="text-xs text-surface-500">{{ borderColor ? 'Border in this color' : 'No border' }}</span>
        </div>
      </div>

      <!-- Divider -->
      <div class="border-t border-surface-200 my-3"></div>

      <!-- Section C: Info (read-only) -->
      <div class="flex flex-col gap-1.5 mb-4">
        <div class="text-xs font-semibold uppercase tracking-wide text-surface-500 mb-1">Image Info</div>

        <div class="flex gap-2 text-sm">
          <span class="text-surface-500 w-32 shrink-0">Filename</span>
          <span class="text-surface-800 break-all">{{ displayFilename }}</span>
        </div>

        <div class="flex gap-2 text-sm">
          <span class="text-surface-500 w-32 shrink-0">Type</span>
          <span class="text-surface-800">{{ sourceLabel }}</span>
        </div>

        <div *ngIf="hasNaturalSize" class="flex gap-2 text-sm">
          <span class="text-surface-500 w-32 shrink-0">Natural size</span>
          <span class="text-surface-800">{{ naturalWidth }} × {{ naturalHeight }} px</span>
        </div>

        <div *ngIf="fileSizeBytes && attrs.imageSourceType === 'attachment'" class="flex gap-2 text-sm">
          <span class="text-surface-500 w-32 shrink-0">File size</span>
          <span class="text-surface-800">{{ formatBytes(fileSizeBytes) }}</span>
        </div>

        <div *ngIf="attrs.originalUrl" class="flex gap-2 text-sm">
          <span class="text-surface-500 w-32 shrink-0">Original URL</span>
          <span class="text-surface-800 break-all text-xs">{{ attrs.originalUrl }}</span>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex gap-2 justify-end">
          <p-button
            label="Cancel"
            severity="secondary"
            (onClick)="onCancel()">
          </p-button>
          <p-button
            label="Save"
            (onClick)="onSave()">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .image-border-preview {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .image-border-preview > span:first-child {
      display: inline-block;
      width: 2.75rem;
      height: 1.75rem;
      border-radius: 2px;
      background: var(--p-surface-100, #f3f4f6);
    }
  `],
})
export class ImageEditDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() attrs: ImageEditAttrs = {};

  /** Attachment metadata — loaded from the attachment service by the parent, not from the Tiptap node. */
  @Input() naturalWidth: number | null = null;
  @Input() naturalHeight: number | null = null;
  @Input() fileSizeBytes: number | null = null;
  /** Attachment's filename from its metadata: null while it's being looked up, '' if not found. */
  @Input() filename: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<ImageEditAttrs>();
  @Output() cancel = new EventEmitter<void>();

  displayWidth: number | null = null;
  displayHeight: number | null = null;
  keepAspectRatio = true;
  borderColor: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true || changes['attrs']) {
      this.syncFromAttrs();
    }
  }

  get hasNaturalSize(): boolean {
    return (this.naturalWidth ?? 0) > 0 && (this.naturalHeight ?? 0) > 0;
  }

  /** The attachment's filename, or for a linked image the last segment of its URL. */
  get displayFilename(): string {
    if (this.filename) return this.filename;
    const src = String(this.attrs.src ?? '');
    const isWeb = /^https?:\/\//i.test(src);
    const url = this.attrs.originalUrl || (this.attrs.imageSourceType === 'linked' || (!this.attrs.imageSourceType && isWeb) ? src : null);
    if (url) {
      try {
        const segment = new URL(url, window.location.href).pathname.split('/').filter(Boolean).pop();
        if (segment) return decodeURIComponent(segment);
      } catch {
        /* not a parseable URL — fall through */
      }
    }
    if (this.attrs.imageSourceType === 'attachment') return this.filename === null ? 'Loading…' : 'Unknown';
    return '—';
  }

  get sourceLabel(): string {
    if (this.attrs.imageSourceType === 'attachment') return 'Downloaded attachment';
    if (this.attrs.imageSourceType === 'linked') return 'Linked (external URL)';
    // Images pasted before pasted images got a source type recorded.
    const src = String(this.attrs.src ?? '');
    if (/^https?:\/\//i.test(src)) return 'Linked (external URL)';
    if (/^data:/i.test(src)) return 'Embedded in note (not an attachment)';
    return 'Unknown';
  }

  get aspectRatio(): number | null {
    const nw = this.naturalWidth ?? 0;
    const nh = this.naturalHeight ?? 0;
    if (nw > 0 && nh > 0) return nw / nh;
    if (this.displayWidth && this.displayHeight) return this.displayWidth / this.displayHeight;
    return null;
  }

  onWidthChange(value: number | null): void {
    if (!this.keepAspectRatio) return;
    const ratio = this.aspectRatio;
    if (ratio && value && value > 0) {
      this.displayHeight = Math.round(value / ratio);
    }
  }

  onHeightChange(value: number | null): void {
    if (!this.keepAspectRatio) return;
    const ratio = this.aspectRatio;
    if (ratio && value && value > 0) {
      this.displayWidth = Math.round(value * ratio);
    }
  }

  onKeepAspectRatioChange(): void {
    // If aspect ratio just turned on and we have width, recalculate height
    if (this.keepAspectRatio && this.displayWidth && this.displayWidth > 0) {
      this.onWidthChange(this.displayWidth);
    }
  }

  borderCss(color: string): string {
    return imageBorderCss(color);
  }

  onBorderColorSelected(event: ColorSelectedEvent): void {
    this.borderColor = normalizeImageBorderColor(event.hex);
  }

  resetToNaturalSize(): void {
    const nw = this.naturalWidth ?? 0;
    const nh = this.naturalHeight ?? 0;
    if (nw > 0 && nh > 0) {
      this.displayWidth = nw;
      this.displayHeight = nh;
    }
  }

  onSave(): void {
    const result: ImageEditAttrs = {
      keepAspectRatio: this.keepAspectRatio,
      borderColor: this.borderColor,
    };

    if (this.displayWidth && this.displayWidth > 0) {
      result.width = `${this.displayWidth}px`;
    } else {
      result.width = null;
    }

    if (this.displayHeight && this.displayHeight > 0) {
      result.height = `${this.displayHeight}px`;
    } else {
      result.height = null;
    }

    this.save.emit(result);
    this.closeDialog();
  }

  onCancel(): void {
    this.cancel.emit();
    this.closeDialog();
  }

  onDialogHide(): void {
    if (this.visible) {
      this.visibleChange.emit(false);
    }
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private syncFromAttrs(): void {
    this.keepAspectRatio = this.attrs.keepAspectRatio !== false;
    this.borderColor = normalizeImageBorderColor(this.attrs.borderColor);
    this.displayWidth = this.parsePxValue(this.attrs.width);
    this.displayHeight = this.parsePxValue(this.attrs.height);
  }

  private parsePxValue(value: string | number | null | undefined): number | null {
    if (!value) return null;
    if (typeof value === 'number') return value > 0 ? Math.round(value) : null;
    const match = String(value).match(/^(\d+(?:\.\d+)?)/);
    const parsed = match ? parseFloat(match[1]) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
